import { Component, ElementRef, EventEmitter, inject, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { InventoryService } from 'src/app/services/inventory.service';
import { STORAGE_KEYS, StorageService } from 'src/app/services/storage.service';

@Component({
  selector: 'app-modal-stock-item',
  template: `
    <ng-template #modalStockItem let-modal>
      <div class="modal-header">
        <h4 class="modal-title" id="modal-basic-title">Agregar inventario</h4>
        <button type="button" class="close btn btn-link" aria-label="Close" (click)="modal.dismiss('Cross click')">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <form [formGroup]="formStockItem" (ngSubmit)="saveStockItem()">
          <div class="form-group mt-4">
            <label for="name" class="form-control-label">Nombre del producto <span class="text-danger">*</span></label>
            <input type="text" class="form-control" formControlName="name" placeholder="Nombre del producto">
            <div *ngIf="ff['name'].touched && ff['name'].invalid">
              <div *ngIf="ff['name'].errors?.['required']" class="text-danger text-sm">El nombre del producto es requerido.</div>
            </div>
          </div>

          <div class="form-group mt-4">
            <label for="sku" class="form-control-label">SKU <span class="text-danger">*</span></label>
            <input type="text" class="form-control" formControlName="sku" placeholder="Código del producto">
            <div *ngIf="ff['sku'].touched && ff['sku'].invalid">
              <div *ngIf="ff['sku'].errors?.['required']" class="text-danger text-sm">El SKU es requerido.</div>
            </div>
          </div>

          <div class="form-group mt-4">
            <label for="quantity" class="form-control-label">Cantidad <span class="text-danger">*</span></label>
            <input type="number" class="form-control" formControlName="quantity" placeholder="Cantidad inicial" min="0">
            <div *ngIf="ff['quantity'].touched && ff['quantity'].invalid">
              <div *ngIf="ff['quantity'].errors?.['required']" class="text-danger text-sm">La cantidad es requerida.</div>
              <div *ngIf="ff['quantity'].errors?.['min']" class="text-danger text-sm">La cantidad debe ser mayor o igual a 0.</div>
            </div>
          </div>

          <div class="form-group mt-4">
            <label for="minimum_threshold" class="form-control-label">Cantidad mínima</label>
            <input type="number" class="form-control" formControlName="minimum_threshold" placeholder="Cantidad mínima en stock" min="0">
            <div *ngIf="ff['minimum_threshold'].touched && ff['minimum_threshold'].invalid">
              <div *ngIf="ff['minimum_threshold'].errors?.['min']" class="text-danger text-sm">La cantidad mínima debe ser mayor o igual a 0.</div>
            </div>
          </div>

          <div class="form-group mt-4">
            <label for="unit_of_measure" class="form-control-label">Unidad de medida</label>
            <select class="form-control" formControlName="unit_of_measure">
              <option value="">Seleccionar unidad</option>
              <option value="pcs">Piezas</option>
              <option value="kg">Kilogramos</option>
              <option value="lbs">Libras</option>
              <option value="m">Metros</option>
              <option value="cm">Centímetros</option>
              <option value="l">Litros</option>
              <option value="ml">Mililitros</option>
              <option value="box">Cajas</option>
              <option value="pack">Paquetes</option>
            </select>
          </div>

          <div class="form-group mt-4">
            <label for="categories" class="form-control-label">Categoría</label>
            <input type="text" class="form-control" formControlName="categories" placeholder="Categoría del producto">
          </div>

          <div class="form-group mt-4">
            <label for="unit_price" class="form-control-label">Precio unitario</label>
            <input type="number" class="form-control" formControlName="unit_price" placeholder="0.00" min="0" step="0.01">
            <div *ngIf="ff['unit_price'].touched && ff['unit_price'].invalid">
              <div *ngIf="ff['unit_price'].errors?.['min']" class="text-danger text-sm">El precio debe ser mayor o igual a 0.</div>
            </div>
          </div>

          <!-- Loading State -->
          <div *ngIf="loading" class="text-center mt-3">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Guardando...</span>
            </div>
          </div>

          <!-- Error Message -->
          <div *ngIf="errorMessage" class="alert alert-danger mt-3" role="alert">
            <i class="fas fa-exclamation-triangle me-2"></i>
            {{ errorMessage }}
          </div>

          <!-- Success Message -->
          <div *ngIf="successMessage" class="alert alert-success mt-3" role="alert">
            <i class="fas fa-check-circle me-2"></i>
            {{ successMessage }}
          </div>

          <div class="form-group mt-4 text-end">
            <button type="button" class="btn btn-secondary me-2" (click)="dismiss()">Cancelar</button>
            <button type="submit" class="btn btn-zazz-dark" [disabled]="formStockItem.invalid || loading">
              <span *ngIf="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              Guardar
            </button>
          </div>
        </form>
      </div>
    </ng-template>
  `,
  styles: [
  ]
})
export class ModalStockItemComponent {
  @Output() updateDismiss: EventEmitter<any> = new EventEmitter<any>();
  @Output() stockCreated: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('modalStockItem') modalStockItem !: ElementRef;

  private modalRef: NgbModalRef | undefined;
  formStockItem: FormGroup = new FormGroup({});

  loading = false;
  errorMessage = '';
  successMessage = '';
  storageService = inject(StorageService);
  constructor(
    private modalService: NgbModal,
    private fb: FormBuilder,
    private inventoryService: InventoryService
  ) { 
    this.initForm();
  }

  initForm() {
    this.formStockItem = this.fb.group({
      name: ['', Validators.required],
      sku: ['', Validators.required],
      quantity: [0, [Validators.required, Validators.min(0)]],
      minimum_threshold: [0, Validators.min(0)],
      unit_of_measure: ['pcs'],
      categories: [''],
      unit_price: [0, Validators.min(0)],
      profile_id: [this.storageService.getItem(STORAGE_KEYS.PROFILE_ID) || this.storageService.getItem(STORAGE_KEYS.PROFILE).profile.id]
    });
  }

  get ff() {
    return this.formStockItem.controls;
  }

  saveStockItem() {
    if (this.formStockItem.valid && !this.loading) {
      this.loading = true;
      this.errorMessage = '';
      this.successMessage = '';

      const formData = this.formStockItem.value;
      
      // Prepare data for API
      const stockData = {
        name: formData.name,
        sku: formData.sku,
        quantity: parseInt(formData.quantity),
        minimum_threshold: parseInt(formData.minimum_threshold) || 0,
        unit_of_measure: formData.unit_of_measure || 'pcs',
        categories: formData.categories || '',
        unit_price: parseFloat(formData.unit_price) || 0,
        profile_id: this.storageService.getItem(STORAGE_KEYS.PROFILE) ? this.storageService.getItem(STORAGE_KEYS.PROFILE).profile.id : null
      };

      this.inventoryService.createStock(stockData).subscribe({
        next: (response: any) => {
          this.loading = false;
          this.successMessage = 'Producto creado exitosamente';
          
          // Emit the created stock item
          this.stockCreated.emit(response);
          
          // Auto-close modal after showing success message
          setTimeout(() => {
            this.dismiss();
          }, 1500);
        },
        error: (error: any) => {
          this.loading = false;
          this.errorMessage = error.error?.message || 'Error al crear el producto';
          console.error('Error creating stock item:', error);
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.formStockItem.controls).forEach(key => {
        this.formStockItem.get(key)?.markAsTouched();
      });
    }
  }

  openModal(modalContent: TemplateRef<any>) {
    this.modalRef = this.modalService.open(modalContent);
  }

  dismiss() {
    if (this.modalRef) {
      this.updateDismiss.emit();
      this.modalRef.dismiss();
    }
  }
}
